import { useEffect, useState } from "react";
import { productUrl } from "../content/roadshow";
import { recordRoadshowEvent } from "../lib/analytics";

function trackCta(choice: string) {
  recordRoadshowEvent({
    sectionId: "cta",
    questionId: "final-cta",
    choice,
    eventType: "cta_click"
  });
}

type QrPreview = {
  id: string;
  title: string;
  src: string;
  alt: string;
  description: string;
  previewLabel: string;
};

const qrPreviews: QrPreview[] = [
  {
    id: "product",
    title: "试用刷题宝",
    src: "/assets/product-qr.png",
    alt: "刷题宝官网二维码",
    description: "打开已备案产品官网，体验刷题宝。",
    previewLabel: "刷题宝"
  },
  {
    id: "wechat",
    title: "公众号",
    src: "/assets/wechat-qr.jpg",
    alt: "微信公众号二维码",
    description: "继续看我的 AI 产品创作过程。",
    previewLabel: "公众号"
  },
  {
    id: "xiaohongshu",
    title: "小红书：云青AI",
    src: "/assets/xiaohongshu-card.jpg",
    alt: "小红书 云青AI 关注二维码",
    description: "适合收藏互动页、路演笔记和产品更新。",
    previewLabel: "小红书"
  }
];

type QrCardProps = {
  preview: QrPreview;
  href?: string;
  onOpenProduct?: () => void;
  onPreview: (preview: QrPreview) => void;
};

function QrCard({ preview, href, onOpenProduct, onPreview }: QrCardProps) {
  const cardBody = (
    <>
      <img src={preview.src} alt={preview.alt} />
      <span className="qr-title">{preview.title}</span>
      <span className="qr-copy">{preview.description}</span>
    </>
  );

  return (
    <div className={`qr-card ${preview.id === "product" ? "product-qr-card" : ""}`}>
      {href ? (
        <a className="qr-card-main" href={href} target="_blank" rel="noreferrer" onClick={onOpenProduct}>
          {cardBody}
        </a>
      ) : (
        <div className="qr-card-main">{cardBody}</div>
      )}
      <button
        className="qr-zoom-button"
        type="button"
        onClick={() => onPreview(preview)}
        aria-label={`查看${preview.previewLabel}二维码大图`}
      >
        查看大图
      </button>
    </div>
  );
}

export function CtaPanel() {
  const [preview, setPreview] = useState<QrPreview | null>(null);
  const productPreview = qrPreviews[0];
  const wechatPreview = qrPreviews[1];
  const xiaohongshuPreview = qrPreviews[2];

  function openPreview(nextPreview: QrPreview) {
    setPreview(nextPreview);
    trackCta(`${nextPreview.id}-preview`);
  }

  useEffect(() => {
    if (!preview) return;

    document.documentElement.classList.add("qr-preview-open");

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setPreview(null);
    }

    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.documentElement.classList.remove("qr-preview-open");
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [preview]);

  return (
    <div className="cta-panel">
      <div className="thanks-panel">
        <p className="eyebrow">一路同行</p>
        <p>
          感谢这些和我一路走来的小伙伴们。刷题宝从一个想法走到可以被真实使用，离不开大家在不同阶段给我的反馈、鼓励和灵感。
        </p>
        <p>
          也欢迎你继续和我交流 AI 产品、学习方法和 vibe coding 的实践。下面可以直接打开产品，或通过公众号、小红书找到我。
        </p>
      </div>

      <div className="cta-grid">
        <QrCard
          preview={productPreview}
          href={productUrl}
          onOpenProduct={() => trackCta("open-product")}
          onPreview={openPreview}
        />
        <QrCard preview={wechatPreview} onPreview={openPreview} />
        <QrCard preview={xiaohongshuPreview} onPreview={openPreview} />
      </div>

      {preview ? (
        <div
          className="qr-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={preview.title}
          onClick={() => setPreview(null)}
        >
          <div className="qr-preview-panel" onClick={(event) => event.stopPropagation()}>
            <button
              className="qr-preview-close"
              type="button"
              onClick={() => setPreview(null)}
              aria-label="关闭二维码预览"
            >
              关闭
            </button>
            <img className="qr-preview-image" src={preview.src} alt={`${preview.alt}大图`} />
            <span className="qr-title">{preview.title}</span>
            <span className="qr-copy">手机端可长按图片保存，再用微信或小红书扫码识别。</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
